import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import {
  FORMATIONS_COLLECTION,
  USERS_COLLECTION,
  firestore,
} from "../lib/firebase";
import type { UserFormationIndex } from "../types/access";
import type {
  Formation,
  FormationAdmin,
  FormationAdminDraft,
  FormationDraft,
  FormationMetadataPatch,
} from "../types/formation";
import { asRecord, toDate } from "../utils/firestoreData";
import { listCurrentUserFormations } from "./accessProjectionService";
import { writeAuditEvent } from "./auditService";

const FORMATION_ADMINS_COLLECTION = "admins";
const USER_FORMATIONS_COLLECTION = "formations";

function normalizeFormation(
  id: string,
  data: Record<string, unknown>,
): Formation {
  return {
    authorUid: typeof data.authorUid === "string" ? data.authorUid : "",
    createdAt: toDate(data.createdAt),
    currentPublishedVersionId:
      typeof data.currentPublishedVersionId === "string"
        ? data.currentPublishedVersionId
        : null,
    deletedAt: toDate(data.deletedAt),
    deletedBy: typeof data.deletedBy === "string" ? data.deletedBy : null,
    description:
      typeof data.description === "string" ? data.description : "",
    id: typeof data.id === "string" ? data.id : id,
    title: typeof data.title === "string" ? data.title : "Formation sans titre",
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeFormationAdmin(
  uid: string,
  data: Record<string, unknown>,
): FormationAdmin {
  return {
    createdAt: toDate(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : "",
    role: "admin",
    uid: typeof data.uid === "string" ? data.uid : uid,
  };
}

const formationConverter: FirestoreDataConverter<Formation> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): Formation {
    return normalizeFormation(snapshot.id, asRecord(snapshot.data(options)));
  },
  toFirestore(value: Formation): DocumentData {
    return value;
  },
};

function normalizeFormationDraft(draft: FormationDraft): FormationDraft {
  return {
    description: draft.description.trim(),
    title: draft.title.trim(),
  };
}

function assertFormationDraft(draft: FormationDraft): FormationDraft {
  const normalizedDraft = normalizeFormationDraft(draft);

  if (!normalizedDraft.title) {
    throw new Error("Le titre de la formation est obligatoire.");
  }

  return normalizedDraft;
}

function userFormationIndexRef(uid: string, formationId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    USERS_COLLECTION,
    uid,
    USER_FORMATIONS_COLLECTION,
    formationId,
  );
}

function formationRef(formationId: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, FORMATIONS_COLLECTION, formationId).withConverter(
    formationConverter,
  );
}

function formationAdminRef(formationId: string, uid: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    FORMATIONS_COLLECTION,
    formationId,
    FORMATION_ADMINS_COLLECTION,
    uid,
  );
}

async function upsertFormationIndex({
  formation,
  role,
  uid,
}: {
  formation: Formation;
  role: UserFormationIndex["role"];
  uid: string;
}): Promise<void> {
  await setDoc(
    userFormationIndexRef(uid, formation.id),
    {
      formationId: formation.id,
      role,
      title: formation.title,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

async function syncFormationIndexes(formation: Formation): Promise<void> {
  const admins = await listFormationAdmins(formation.id);

  await Promise.all([
    upsertFormationIndex({
      formation,
      role: "author",
      uid: formation.authorUid,
    }),
    ...admins.map((admin) =>
      upsertFormationIndex({
        formation,
        role: "admin",
        uid: admin.uid,
      }),
    ),
  ]);
}

async function deleteFormationIndexes(formation: Formation): Promise<void> {
  const admins = await listFormationAdmins(formation.id);
  const targetUids = new Set([
    formation.authorUid,
    ...admins.map((admin) => admin.uid),
  ]);

  await Promise.all(
    [...targetUids].map((targetUid) =>
      deleteDoc(userFormationIndexRef(targetUid, formation.id)),
    ),
  );
}

export async function createFormation({
  actorUid,
  draft,
}: {
  actorUid: string;
  draft: FormationDraft;
}): Promise<Formation> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const normalizedDraft = assertFormationDraft(draft);
  const newFormationRef = doc(collection(firestore, FORMATIONS_COLLECTION));
  const formationId = newFormationRef.id;

  await setDoc(newFormationRef, {
    authorUid: actorUid,
    createdAt: serverTimestamp(),
    description: normalizedDraft.description,
    id: formationId,
    title: normalizedDraft.title,
    updatedAt: serverTimestamp(),
  });
  await setDoc(userFormationIndexRef(actorUid, formationId), {
    formationId,
    role: "author",
    title: normalizedDraft.title,
    updatedAt: serverTimestamp(),
  });
  await writeAuditEvent({
    actorUid,
    eventType: "formation.created",
    metadata: {
      formationId,
      title: normalizedDraft.title,
    },
  });

  const snapshot = await getDoc(formationRef(formationId));

  if (!snapshot.exists()) {
    throw new Error("La formation créée est introuvable.");
  }

  return snapshot.data();
}

export async function readFormation(
  formationId: string,
): Promise<Formation | null> {
  const snapshot = await getDoc(formationRef(formationId));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function listAccessibleFormations({
  isGlobalAdmin,
  uid,
}: {
  isGlobalAdmin: boolean;
  uid: string;
}): Promise<Formation[]> {
  if (!firestore) {
    return [];
  }

  if (isGlobalAdmin) {
    const formationsQuery = query(
      collection(firestore, FORMATIONS_COLLECTION).withConverter(
        formationConverter,
      ),
      orderBy("updatedAt", "desc"),
    );
    const snapshot = await getDocs(formationsQuery);
    return snapshot.docs
      .map((formationDoc) => formationDoc.data())
      .filter((formation) => formation.deletedAt === null);
  }

  const indexes = await listCurrentUserFormations(uid);
  const formations = await Promise.all(
    indexes.map((index) => readFormation(index.formationId)),
  );

  return formations.filter(
    (formation): formation is Formation =>
      formation !== null && formation.deletedAt === null,
  );
}

export async function updateFormationMetadata({
  actorUid,
  formationId,
  patch,
}: {
  actorUid: string;
  formationId: string;
  patch: FormationMetadataPatch;
}): Promise<Formation> {
  const normalizedPatch = assertFormationDraft(patch);

  await updateDoc(formationRef(formationId), {
    description: normalizedPatch.description,
    title: normalizedPatch.title,
    updatedAt: serverTimestamp(),
  });

  const nextFormation = await readFormation(formationId);

  if (!nextFormation) {
    throw new Error("Formation introuvable après modification.");
  }

  await syncFormationIndexes(nextFormation);
  await writeAuditEvent({
    actorUid,
    eventType: "formation.updated",
    metadata: {
      formationId,
      title: normalizedPatch.title,
    },
  });

  return nextFormation;
}

export async function softDeleteFormation({
  actorUid,
  formationId,
}: {
  actorUid: string;
  formationId: string;
}): Promise<void> {
  const formation = await readFormation(formationId);

  if (!formation) {
    throw new Error("Formation introuvable.");
  }

  await updateDoc(formationRef(formationId), {
    deletedAt: serverTimestamp(),
    deletedBy: actorUid,
    updatedAt: serverTimestamp(),
  });
  await deleteFormationIndexes(formation);
  await writeAuditEvent({
    actorUid,
    eventType: "formation.deleted",
    metadata: {
      formationId,
    },
  });
}

export async function listFormationAdmins(
  formationId: string,
): Promise<FormationAdmin[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    collection(
      firestore,
      FORMATIONS_COLLECTION,
      formationId,
      FORMATION_ADMINS_COLLECTION,
    ),
  );

  return snapshot.docs
    .map((adminDoc) => normalizeFormationAdmin(adminDoc.id, asRecord(adminDoc.data())))
    .sort((left, right) => left.uid.localeCompare(right.uid, "fr"));
}

export async function addFormationAdmin({
  actorUid,
  draft,
  formation,
}: {
  actorUid: string;
  draft: FormationAdminDraft;
  formation: Formation;
}): Promise<FormationAdmin> {
  const adminUid = draft.uid.trim();

  if (!adminUid) {
    throw new Error("L'UID administrateur est obligatoire.");
  }

  await setDoc(formationAdminRef(formation.id, adminUid), {
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    role: "admin",
    uid: adminUid,
  });
  await upsertFormationIndex({
    formation,
    role: "admin",
    uid: adminUid,
  });
  await writeAuditEvent({
    actorUid,
    eventType: "formation.admin.added",
    metadata: {
      adminUid,
      formationId: formation.id,
    },
  });

  const snapshot = await getDoc(formationAdminRef(formation.id, adminUid));
  return normalizeFormationAdmin(adminUid, asRecord(snapshot.data()));
}

export async function removeFormationAdmin({
  actorUid,
  formationId,
  targetUid,
}: {
  actorUid: string;
  formationId: string;
  targetUid: string;
}): Promise<void> {
  await deleteDoc(userFormationIndexRef(targetUid, formationId));
  await deleteDoc(formationAdminRef(formationId, targetUid));
  await writeAuditEvent({
    actorUid,
    eventType: "formation.admin.removed",
    metadata: {
      formationId,
      targetUid,
    },
  });
}
